import React, {
  useEffect,
  useState,
  useMemo,
  useCallback,
  useRef,
} from 'react';
import {
  Box,
  Paper,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Popover,
  Typography,
} from '@mui/material';
import LayersIcon from '@mui/icons-material/Layers';

import { CustomButton } from '../../components/Infra/CustomButton/CustomButton';
import PageHeader from '../../components/PageHeader/PageHeader';
import {
  useGridApiRef,
  GridColumnMenu,
  GRID_TREE_DATA_GROUPING_FIELD,
  GRID_CHECKBOX_SELECTION_COL_DEF,
  GridPinnedColumns,
  GridSortModel,
  GridFilterModel,
} from '@mui/x-data-grid-premium';
import StyledDataGrid from '../../components/DataGrid/DataGridStyles';
import { CustomGridFooter } from '../../components/DataGrid/CustomGridFooter';

import { useTableSearch } from '../../hooks/useTableSearch';
import { useViewManagement } from '../../hooks/useViewManagement/useViewManagement';
import UndoRedoButtons from '../../components/UndoRedo/UndoRedoButtons';
import AutosizeColumnsToggle from '../../components/DataGrid/AutosizeColumnsToggle';
import GroupToggle from '../../components/DataGrid/GroupToggle';
import InactiveInstrumentsToggle from '../../components/DataGrid/InactiveInstrumentsToggle';
import Toolbar from '../../components/Toolbar/Toolbar';
import AddIcon from '@mui/icons-material/Add';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import AddColumnModal from '../../components/Modal/AddColumnModal';
import { BASE_COLUMN_FIELDS, CSS_CLASSES, DATE_FORMATS } from './OTCConstants';
import SavedViewsPanel from '../../components/Views/SavedViewsPanel';
import SaveViewDialog from '../../components/Views/SaveViewDialog';
import ViewContextMenu from '../../components/Views/ViewContextMenu';
import ActiveViewIndicator from '../../components/Views/ActiveViewIndicator';
import { useViewState } from '../../store/viewStore';
import { PAGE_TYPES } from '../../constants/pageTypes';
import ViewListIcon from '@mui/icons-material/ViewList';
import SaveIcon from '@mui/icons-material/Save';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import DeleteIcon from '@mui/icons-material/Delete';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import SyncIcon from '@mui/icons-material/Sync';
import ConfirmationModal from '../../components/Modal/ConfirmationModal';
import CloudDownload from '@mui/icons-material/CloudDownload';
import ManageGroupsModal from '../../components/Groups/ManageGroupsModal';
import DevelopmentWarningBanner from '../../components/DevelopmentWarningBanner/DevelopmentWarningBanner';
import EmptyState from '../../components/EmptyState/EmptyState';
import {
  transformDataForGrouping,
  getTreeDataPath,
  convertGridColumnsToGroupColumns,
} from '../../components/Groups/groupUtils';
import { useGroupingColDef } from '../../components/Groups/useManageGroups';
import { DATAGRID_STYLES } from '../../components/DataGrid/DataGridUtils';
import { showSnackbar } from '../../context/Snackbar/SnackbarContext';
import { SnackbarType } from '../../context/Snackbar/Types';
import { createPinnedColumnsHandler } from '../../utils/gridPinnedColumnsHelper';
import { formatNumberWithCommas } from '../../utils/numberFormatter';
import {
  createStateChangeHandler,
  useFilteredSelectionModel,
} from '../../components/Groups/gridStateHelper';
import { useGroupCheckboxHandler } from '../../components/Groups/useGroupCheckboxHandler';

// Import all OTC hooks
import { useOTCData } from './hooks/useOTCData';
import { useOTCColumns } from './hooks/useOTCColumns';
import { useOTCIllio } from './hooks/useOTCIllio';
import { useOTCOperations } from './hooks/useOTCOperations';
import { useOTCBackup } from './hooks/useOTCBackup';
import { useOTCFilters } from './hooks/useOTCFilters';
import {
  OTCRow,
  ExtendedGridColumnMenuProps,
  DeleteColumnMenuItemProps,
  DAYS_SINCE_ILLIO_UPDATE,
} from './types';
import {
  getRowId,
  createGridSelectionModel,
  formatLastSyncDate,
} from './utils';
import { OTCSyncProgress } from './components/OTCSyncProgress/OTCSyncProgress';
import { OTCCheckProgress } from './components/OTCCheckProgress/OTCCheckProgress';

// Create the DeleteColumnMenuItem component
function DeleteColumnMenuItem(props: DeleteColumnMenuItemProps) {
  const { onDelete } = props;

  const handleClick = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    onDelete();
  };

  return (
    <MenuItem onClick={handleClick}>
      <ListItemIcon>
        <DeleteIcon fontSize="small" />
      </ListItemIcon>
      <ListItemText>Delete Column</ListItemText>
    </MenuItem>
  );
}

// Update the CustomColumnMenu component
function CustomColumnMenu(props: ExtendedGridColumnMenuProps) {
  const { colDef, onDeleteColumnClick } = props;

  const isLabelColumn =
    !DATE_FORMATS.YYYY_MM_DD_REGEX.test(colDef.field) &&
    !BASE_COLUMN_FIELDS.includes(colDef.field);

  const handleDeleteColumn = (event?: React.MouseEvent) => {
    // Prevent event propagation if event is provided
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    // Open confirmation modal instead of deleting directly
    if (onDeleteColumnClick) {
      onDeleteColumnClick(colDef.field, colDef.headerName || colDef.field);
    }
  };

  // Always return the default menu with custom additions for label columns
  return (
    <GridColumnMenu
      {...props}
      slots={{
        // Only add delete option for label columns, preserve all default items
        ...(isLabelColumn && {
          columnMenuUserItem: DeleteColumnMenuItem,
        }),
      }}
      slotProps={{
        ...(isLabelColumn && {
          columnMenuUserItem: {
            displayOrder: 999,
            onDelete: handleDeleteColumn,
          },
        }),
      }}
    />
  );
}

export const OTCPage: React.FC = () => {
  // Track when we're programmatically updating the grid to avoid false modifications
  // Start as true to prevent modification tracking during initial page load and view restoration
  const [isProgrammaticUpdate, setIsProgrammaticUpdate] = useState(true);
  const apiRef = useGridApiRef();

  // Track if component is mounted to prevent state updates after unmount
  const isMountedRef = useRef(true);

  // Ref to preserve grid state during remounting (when grouping is toggled)
  const preservedStateRef = useRef<{
    pinnedColumns?: GridPinnedColumns;
    sortModel?: GridSortModel;
    filterModel?: GridFilterModel;
    columnVisibility?: Record<string, boolean>;
  } | null>(null);

  // State to track current display data for selection handler
  const [currentDisplayData, setCurrentDisplayData] = useState<OTCRow[]>([]);

  // Initialize all OTC hooks with proper dependencies
  const otcDataHook = useOTCData({ apiRef, displayData: currentDisplayData });

  const {
    otcData,
    originalData,
    loading,
    selectedRows,
    lastSyncDate,
    showInactiveInstruments,
    setShowInactiveInstruments,
    groups,
    setGroups,
    isGroupingEnabled,
    isGroupsModalOpen,
    availableRowsForGroups,
    fetchOTCData,
    setOTCData,
    setOriginalData,
    handleRowSelectionChange,
    setSelectedRows,
    handleOpenGroupsModal,
    handleCloseGroupsModal,
    handleSaveGroups,
    setIsGroupingEnabled,
    loadGroupsFromBackend,
    calculatePnL,
    calculateDaysSinceIllioUpdate,
    expandGroups,
  } = otcDataHook;

  // Search functionality
  const {
    filteredData: searchFilteredData,
    searchTerm,
    handleSearch,
  } = useTableSearch<OTCRow>(otcData);

  // Get view state for accessing store operations
  const viewState = useViewState(PAGE_TYPES.OTC);

  // Initialize filters
  const [autosizeColumns, setAutosizeColumns] = useState<boolean>(false);
  const [hideEmptyDateColumns, setHideEmptyDateColumns] =
    useState<boolean>(false);

  // Controlled sort model state - persists through search/filter changes
  const [sortModel, setSortModel] = useState<GridSortModel>([]);

  // State for Illio value tooltip popover
  const [tooltipAnchor, setTooltipAnchor] = useState<HTMLElement | null>(null);
  const [tooltipValue, setTooltipValue] = useState<string>('');

  // Ref to track if active view has been restored from localStorage
  const hasRestoredViewRef = useRef(false);

  // Add view management hook (moved before useOTCColumns to fix initialization order)
  const viewManager = useViewManagement({
    pageType: PAGE_TYPES.OTC,
    apiRef,
    searchTerm,
    selectedRows,
    autosizeColumns,
    displayData: searchFilteredData, // Will be updated by filters hook
    handleSearch,
    setSelectedRows,
    setAutosizeColumns,
    isGroupingEnabled,
    setIsGroupingEnabled,
    groups,
    setGroups,
  });

  // Initialize columns hook
  const otcColumnsHook = useOTCColumns({
    otcData,
    autosizeColumns,
    isGroupingEnabled,
    calculatePnL,
    apiRef,
    viewManager,
  });

  const {
    columns,
    modalOpen,
    deleteColumnConfirmation,
    hiddenColumnFields,
    setColumns,
    setModalOpen,
    setHiddenColumnFields,
    handleAddColumn,
    handleDeleteColumnClick,
    handleConfirmDeleteColumn,
    handleColumnVisibilityChange,
    getCurrentDateColumns,
    setDeleteColumnConfirmation,
  } = otcColumnsHook;

  // Initialize Illio hook
  const otcIllioHook = useOTCIllio({
    otcData,
    originalData,
    selectedRows,
    setOTCData,
    setOriginalData,
    setSelectedRows,
    fetchOTCData,
    calculateDaysSinceIllioUpdate,
    showInactiveInstruments,
  });

  const {
    updatedCells,
    cellStatusMap,
    illioValuesMap,
    illioCheckLoading,
    illioUploadLoading,
    syncLoading,
    showOnlyIllioDifferences,
    setShowOnlyIllioDifferences,
    showSyncConfirmation,
    setShowSyncConfirmation,
    checkAllIllioDifferences,
    failedOtcIds,
    retryFailedOtcCheck,
    checkDifferenceWithIllioForRow,
    handleIllioUpload,
    handleSyncClick,
    confirmSyncFromIllio,
    getCellClassName,
    toggleIllioDifferencesFilter,
    clearUpdatedCellsForRows,
    clearIndicatorForCell,
  } = otcIllioHook;

  // Initialize operations hook
  const otcOperationsHook = useOTCOperations({
    otcData,
    originalData,
    setOTCData,
    columns,
    checkDifferenceWithIllioForRow,
    calculatePnL,
    clearUpdatedCellsForRows,
    setOriginalData,
    fetchOTCData,
    selectedRows,
    setSelectedRows,
  });

  const {
    processRowUpdate,
    undo,
    redo,
    canUndo,
    canRedo,
    getUndoActionInfo,
    getRedoActionInfo,
  } = otcOperationsHook;

  // Initialize backup hook
  const otcBackupHook = useOTCBackup({
    fetchOTCData,
    setOTCData,
    clearAllIllioComparisons: otcIllioHook.clearAllIllioComparisons,
    setSelectedRows,
    showInactiveInstruments,
  });

  const {
    isDownloadingBackup,
    isRestoringBackup,
    handleDownloadBackup,
    handleRestoreBackup,
  } = otcBackupHook;

  // Set mounted flag and cleanup
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Critical fix: Load data on component mount with current inactive filter setting
  useEffect(() => {
    fetchOTCData(showInactiveInstruments);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount - useOTCData handles subsequent showInactiveInstruments changes

  // Function to capture current grid state before remounting
  const captureGridState = useCallback(() => {
    if (apiRef.current) {
      preservedStateRef.current = {
        pinnedColumns: apiRef.current.getPinnedColumns?.() || {
          left: [],
          right: [],
        },
        sortModel: apiRef.current.getSortModel?.() || [],
        filterModel: apiRef.current.getFilterModel?.() || { items: [] },
        columnVisibility: apiRef.current.getColumnVisibilityModel?.() || {},
      };
    }
  }, []);

  // Wrapper function to capture state before toggling groups
  const handleToggleGrouping = useCallback(
    (enabled: boolean) => {
      // Capture current state before the toggle causes remounting
      captureGridState();
      // Then toggle grouping
      setIsGroupingEnabled(enabled);
    },
    [captureGridState, setIsGroupingEnabled]
  );

  // Auto-resize columns when autosize toggle changes
  useEffect(() => {
    if (autosizeColumns && apiRef.current && columns.length > 0) {
      // Use setTimeout to ensure columns are rendered
      const timer = setTimeout(() => {
        if (apiRef.current?.autosizeColumns) {
          // Build column list for autosize - include grouping columns when grouping is enabled
          const columnsToAutosize = [...BASE_COLUMN_FIELDS];
          if (isGroupingEnabled) {
            columnsToAutosize.push(GRID_TREE_DATA_GROUPING_FIELD, 'group_name');
          }

          apiRef.current.autosizeColumns({
            includeHeaders: true,
            includeOutliers: true,
            expand: true,
            columns: columnsToAutosize,
          });
        }
      }, 100);
      return () => clearTimeout(timer);
    }
    // Don't reset widths on every render - only when toggling autosize off
  }, [autosizeColumns, isGroupingEnabled, groups]);

  // Restore preserved state after remount (when grouping is toggled)
  useEffect(() => {
    // Only restore if we have preserved state and the grid is ready
    if (preservedStateRef.current && apiRef.current) {
      // Use a timeout to ensure grid is fully mounted
      const timeout = setTimeout(() => {
        const state = preservedStateRef.current!;

        // Restore sort model
        if (state.sortModel && apiRef.current) {
          setSortModel(state.sortModel);
          apiRef.current.setSortModel(state.sortModel);
        }

        // Restore filter model
        if (state.filterModel && apiRef.current) {
          apiRef.current.setFilterModel(state.filterModel);
        }

        // Column visibility is now controlled via hiddenColumnFields state, no need to restore here

        // Handle pinned columns with protected column adjustments
        if (state.pinnedColumns && apiRef.current) {
          // Always ensure checkbox column is first, then add group columns if grouping is enabled
          const protectedColumns = [GRID_CHECKBOX_SELECTION_COL_DEF.field];

          if (isGroupingEnabled) {
            protectedColumns.push(GRID_TREE_DATA_GROUPING_FIELD, 'group_name');
          }

          // Extract user columns from preserved state by filtering out all protected columns
          const userColumns = (state.pinnedColumns.left || []).filter(
            col => !protectedColumns.includes(col)
          );

          // Always rebuild with protected columns first, then user columns
          const correctedLeft = [...protectedColumns, ...userColumns];

          apiRef.current.setPinnedColumns({
            left: correctedLeft,
            right: state.pinnedColumns.right || [],
          });
        }

        // Clear preserved state after restoration
        preservedStateRef.current = null;
      }, 0);

      return () => clearTimeout(timeout);
    }
  }, [isGroupingEnabled]);

  // Override applyPinnedColumns to ensure protected columns are always first
  const originalApplyPinnedColumns = viewManager.applyPinnedColumns;
  viewManager.applyPinnedColumns = useCallback(
    createPinnedColumnsHandler(originalApplyPinnedColumns, isGroupingEnabled),
    [originalApplyPinnedColumns, isGroupingEnabled]
  );

  // Custom comparison function that includes OTC-specific state
  const compareWithAppliedView = useCallback(() => {
    if (!viewManager) return [];
    if (!viewManager.activeView) return [];

    // Get base modifications from viewManager
    const baseModifications = viewManager.compareWithAppliedView();
    const modifications = [...baseModifications];

    // Add OTC-specific comparisons
    const activeView = viewManager.activeView as any;

    if (
      showOnlyIllioDifferences !==
      (activeView.showOnlyIllioDifferences || false)
    ) {
      modifications.push(
        `Show only Illio differences: ${showOnlyIllioDifferences ? 'Yes' : 'No'}`
      );
    }

    if (hideEmptyDateColumns !== (activeView.hideEmptyDateColumns || false)) {
      modifications.push(
        `Hide empty dates: ${hideEmptyDateColumns ? 'Yes' : 'No'}`
      );
    }

    if (
      showInactiveInstruments !== (activeView.showInactiveInstruments || false)
    ) {
      modifications.push(
        `Show inactive instruments: ${showInactiveInstruments ? 'Yes' : 'No'}`
      );
    }

    return modifications;
  }, [viewManager, showOnlyIllioDifferences, hideEmptyDateColumns]);

  // Update view modifications tracking
  const updateViewModifications = useCallback(() => {
    if (!isProgrammaticUpdate && viewManager && viewManager.activeView) {
      const modifications = compareWithAppliedView();
      viewManager.setViewModifications(modifications);
    }
  }, [isProgrammaticUpdate, viewManager, compareWithAppliedView]);

  // Initialize filters hook
  // Pass viewManager.filteredDisplayData instead of searchFilteredData to include view row filtering
  const otcFiltersHook = useOTCFilters({
    otcData,
    searchFilteredData: viewManager.filteredDisplayData as OTCRow[],
    updatedCells,
    columns,
    hiddenColumnFields,
    apiRef,
    setHiddenColumnFields,
    updateViewModifications,
    viewManager,
    isGroupingEnabled,
    showOnlyIllioDifferences,
    hideEmptyDateColumns,
    setHideEmptyDateColumns,
  });

  const {
    filteredData,
    toggleHideEmptyDateColumns,
    applyHideEmptyDateColumns,
    handleExport,
  } = otcFiltersHook;

  // Transform data for grouping (filtering is now done by backend)
  const baseDisplayData = useMemo(() => {
    return transformDataForGrouping(filteredData, groups, isGroupingEnabled);
  }, [filteredData, groups, isGroupingEnabled]);

  // Calculate actual data row count (excluding group headers)
  const actualRowCount = useMemo(() => {
    return baseDisplayData.filter(row => !row.isGroup).length;
  }, [baseDisplayData]);

  // Update currentDisplayData for selection handler whenever baseDisplayData changes
  useEffect(() => {
    setCurrentDisplayData(baseDisplayData);
  }, [baseDisplayData]);

  // Get grouping column definition
  const groupingColDef = useGroupingColDef(isGroupingEnabled);

  // Group checkbox click handler
  const handleCellClick = useGroupCheckboxHandler({
    displayData: baseDisplayData,
    selectedRows,
    expandGroups,
    onSelectionChange: handleRowSelectionChange,
    getRowId: row => getRowId(row),
  });

  // Handle cell mouse enter for Illio value tooltip
  const handleCellMouseEnter = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      const field = event.currentTarget.dataset.field;
      const rowId = event.currentTarget.parentElement?.dataset.id;

      if (!field || !rowId) return;

      // Only show tooltip for cells with indicators
      if (!updatedCells[rowId]?.has(field)) return;

      // Remove native browser tooltip to avoid showing two tooltips
      event.currentTarget.removeAttribute('title');

      const illioValue = illioValuesMap[rowId]?.[field];

      // Format the value - show "Empty in Illio" for null values (ADDED status)
      const formattedValue =
        illioValue === undefined || illioValue === null
          ? 'Empty in Illio'
          : typeof illioValue === 'number'
            ? formatNumberWithCommas(illioValue, 4, true)
            : String(illioValue);

      setTooltipValue(formattedValue);
      setTooltipAnchor(event.currentTarget);
    },
    [updatedCells, illioValuesMap]
  );

  // Handle cell mouse leave for Illio value tooltip
  const handleCellMouseLeave = useCallback(() => {
    setTooltipAnchor(null);
  }, []);

  // Handle cell double-click to paste Illio value
  const handleCellDoubleClick = useCallback(
    (params: any, event: any) => {
      const rowId = params.id.toString();
      const field = params.field;

      // Check if cell has an indicator
      if (!updatedCells[rowId]?.has(field)) return;

      // Get Illio value
      const illioValue = illioValuesMap[rowId]?.[field];
      if (illioValue === undefined) return;

      // Prevent entering edit mode
      event.defaultMuiPrevented = true;

      // Find the row and update it
      const row = otcData.find(r => getRowId(r) === rowId);
      if (!row) return;

      const updatedRow = { ...row, [field]: illioValue };
      processRowUpdate(updatedRow);

      // Clear the indicator for this cell
      clearIndicatorForCell(rowId, field);

      // Close tooltip if open
      setTooltipAnchor(null);
    },
    [
      updatedCells,
      illioValuesMap,
      otcData,
      processRowUpdate,
      clearIndicatorForCell,
    ]
  );

  // Custom wrapper for applying views with OTC-specific state
  const applyOTCView = useCallback(
    async (view: any) => {
      let timeoutId: NodeJS.Timeout | null = null;

      // Return cleanup function to cancel pending operations
      const cleanup = () => {
        if (timeoutId !== null) {
          clearTimeout(timeoutId);
          timeoutId = null;
        }
      };

      try {
        if (!isMountedRef.current) return cleanup;

        setIsProgrammaticUpdate(true);

        // Sync hiddenColumnFields with view's column visibility BEFORE applying view
        // This is critical because the DataGrid uses hiddenColumnFields to build columnVisibilityModel prop
        if (view.columnVisibility) {
          const newHiddenFields = new Set<string>();
          Object.entries(view.columnVisibility).forEach(
            ([field, isVisible]) => {
              if (isVisible === false) {
                newHiddenFields.add(field);
              }
            }
          );
          setHiddenColumnFields(newHiddenFields);
        } else {
          // If no column visibility in view, clear hidden fields
          setHiddenColumnFields(new Set());
        }

        // First apply base view state using viewManager
        await viewManager.applyView(view);

        // Check if still mounted after async operation
        if (!isMountedRef.current) return cleanup;

        // Then apply OTC-specific state
        setShowOnlyIllioDifferences(view.showOnlyIllioDifferences || false);

        // Apply hideEmptyDateColumns - only call applyHideEmptyDateColumns when true
        // because applyHideEmptyDateColumns(false) clears ALL hidden date columns,
        // which would override manually hidden columns from view.columnVisibility
        if (view.hideEmptyDateColumns) {
          applyHideEmptyDateColumns(true);
        } else {
          // Just set the state directly without triggering the column visibility logic
          setHideEmptyDateColumns(false);
        }

        // Apply showInactiveInstruments
        setShowInactiveInstruments(view.showInactiveInstruments || false);

        // Re-apply sort after column visibility changes to ensure it sticks
        // Column visibility changes can cause MUI DataGrid to clear the sort
        if (view.sortModel && view.sortModel.length > 0) {
          setSortModel(view.sortModel);
          await new Promise<void>(resolve => {
            requestAnimationFrame(() => {
              if (!isMountedRef.current) {
                resolve();
                return;
              }
              if (apiRef.current) {
                apiRef.current.setSortModel(view.sortModel);
              }
              resolve();
            });
          });
        }

        // Check if still mounted before setting timeout
        if (!isMountedRef.current) return cleanup;

        // Clear the programmatic update flag with longer delay to ensure sort settles
        timeoutId = setTimeout(() => {
          if (!isMountedRef.current) return;
          setIsProgrammaticUpdate(false);
        }, 300);

        return cleanup;
      } catch (error) {
        console.error('Error applying OTC view:', error);
        if (isMountedRef.current) {
          setIsProgrammaticUpdate(false);
        }
        return cleanup;
      }
    },
    [
      viewManager,
      setShowOnlyIllioDifferences,
      applyHideEmptyDateColumns,
      setHiddenColumnFields,
      setHideEmptyDateColumns,
      columns,
      apiRef,
    ]
  );

  // Restore active view on mount with OTC-specific state
  useEffect(() => {
    let cleanup: (() => void) | undefined;

    // Only run once on mount when view is loaded from localStorage
    if (
      viewManager.activeView &&
      !hasRestoredViewRef.current &&
      otcData.length > 0
    ) {
      hasRestoredViewRef.current = true;
      const applyPromise = applyOTCView(viewManager.activeView);

      // Store cleanup function from applyOTCView
      applyPromise.then(cleanupFn => {
        cleanup = cleanupFn;
      });
    }

    // Cancel pending operations when component unmounts or effect re-runs
    return () => {
      if (cleanup) {
        cleanup();
      }
    };
  }, [viewManager.activeView, otcData.length, applyOTCView]);

  // Update the handleClearActiveView function to work with the view manager
  const handleClearActiveView = useCallback(() => {
    if (!viewManager) return;

    setIsProgrammaticUpdate(true);

    // Reset all filters and selections
    setSelectedRows([]);
    setShowOnlyIllioDifferences(false);
    setHideEmptyDateColumns(false);
    setAutosizeColumns(false);
    setIsGroupingEnabled(false);
    setHiddenColumnFields(new Set());
    setSortModel([]);

    // Clear active view in view manager
    viewManager.clearActiveView();

    setTimeout(() => {
      setIsProgrammaticUpdate(false);
    }, 100);
  }, [
    viewManager,
    setSelectedRows,
    setShowOnlyIllioDifferences,
    setHideEmptyDateColumns,
    setAutosizeColumns,
    setIsGroupingEnabled,
    setHiddenColumnFields,
  ]);

  // Custom save function that includes OTC-specific state (matches oldOtc.tsx pattern)
  const saveView = useCallback(
    (viewName: string, autoShowNewDatesValue?: boolean) => {
      const baseView = {
        id:
          viewManager.editingView?.id ||
          `view-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        name: viewName,
        searchTerm,
        // Force boolean conversion to prevent any array values
        autosizeColumns: autosizeColumns === true,
        selectedRows: selectedRows.length > 0 ? selectedRows : undefined,
        showOnlyIllioDifferences,
        hideEmptyDateColumns,
        autoShowNewDates: viewManager.editingView
          ? (viewManager.activeView as any)?.autoShowNewDates
          : (autoShowNewDatesValue ?? true),
        originalDateColumns: viewManager.editingView
          ? (viewManager.activeView as any)?.originalDateColumns
          : getCurrentDateColumns(),
        // Force boolean for isGroupingEnabled
        isGroupingEnabled: isGroupingEnabled === true,
        groups: Array.isArray(groups) ? groups : [],
      };

      // Create view with current grid state
      const newView = {
        ...baseView,
        columnFilters: viewManager.getColumnFilters(),
        sortModel: viewManager.getSortModel(),
        columnVisibility: viewManager.getColumnVisibility(),
        pinnedColumns: viewManager.getPinnedColumns(),
      };

      // Save using viewState methods
      if (viewManager.editingView) {
        viewState.updateViewById(viewManager.editingView.id, newView);
        if (viewState.activeView?.id === viewManager.editingView.id) {
          viewState.setActiveView(newView);
          viewState.setViewModifications([]);
        }
      } else {
        viewState.addView(newView);
        viewState.setActiveView(newView);
        viewState.setViewModifications([]);
      }

      viewManager.closeSaveDialog();

      // Show success message
      showSnackbar({
        title: 'Success',
        message: `View "${viewName}" ${viewManager.editingView ? 'updated' : 'saved'} successfully`,
        type: SnackbarType.success,
      });
    },
    [
      viewManager,
      searchTerm,
      autosizeColumns,
      selectedRows,
      showOnlyIllioDifferences,
      hideEmptyDateColumns,
      isGroupingEnabled,
      groups,
      getCurrentDateColumns,
      viewState,
    ]
  );

  const groupModalColumns = useMemo(
    () => convertGridColumnsToGroupColumns(columns),
    [columns]
  );

  // Hide non-essential columns in groups modal: date columns, calculated fields, and group column
  const modalHiddenColumns = useMemo(() => {
    const hidden = new Set(['pnl', DAYS_SINCE_ILLIO_UPDATE, 'group_name']);
    // Add all date columns to keep modal focused on identifying columns
    columns.forEach(col => {
      if (DATE_FORMATS.YYYY_MM_DD_REGEX.test(col.field)) {
        hidden.add(col.field);
      }
    });
    return Array.from(hidden);
  }, [columns]);

  // Create filtered rowSelectionModel to preserve selections during search
  const getRowIdCallback = useCallback((row: OTCRow) => row.id, []);
  const gridSelectionModel = useFilteredSelectionModel(
    Array.isArray(selectedRows) ? selectedRows : [],
    baseDisplayData,
    getRowIdCallback
  );

  // Initialize isProgrammaticUpdate to false after initial load
  useEffect(() => {
    const timer = setTimeout(() => {
      // Only update state if component is still mounted
      if (isMountedRef.current) {
        setIsProgrammaticUpdate(false);
      }
    }, 1000); // Give time for initial data loading and view restoration

    return () => clearTimeout(timer);
  }, []);

  // Track modifications when OTC-specific state changes
  useEffect(() => {
    if (viewManager && viewManager.activeView) {
      // Use a timeout to debounce updates and prevent infinite loops
      const timeoutId = setTimeout(() => {
        updateViewModifications();
      }, 50);
      return () => clearTimeout(timeoutId);
    }
  }, [
    showOnlyIllioDifferences,
    hideEmptyDateColumns,
    selectedRows,
    searchTerm,
    autosizeColumns,
    isGroupingEnabled,
    viewManager,
    updateViewModifications,
  ]);

  // Handle column visibility changes
  const handleColumnVisibilityChangeWrapper = (
    newVisibility: Record<string, boolean>
  ) => {
    handleColumnVisibilityChange(newVisibility);
    updateViewModifications();
  };

  // Handle updating the current active view with current state
  const handleUpdateCurrentView = useCallback(() => {
    if (!viewManager || !viewManager.activeView) return;

    const currentView = viewManager.activeView;

    // Create updated view with current grid state
    const baseView = {
      id: currentView.id, // Use EXISTING ID from active view
      name: currentView.name,
      searchTerm,
      autosizeColumns: autosizeColumns === true,
      selectedRows: selectedRows.length > 0 ? selectedRows : undefined,
      showOnlyIllioDifferences,
      hideEmptyDateColumns,
      autoShowNewDates: currentView?.autoShowNewDates ?? true,
      originalDateColumns:
        currentView?.originalDateColumns ?? getCurrentDateColumns(),
      isGroupingEnabled: isGroupingEnabled === true,
      groups: Array.isArray(groups) ? groups : [],
    };

    const updatedView = {
      ...baseView,
      columnFilters: viewManager.getColumnFilters(),
      sortModel: viewManager.getSortModel(),
      columnVisibility: viewManager.getColumnVisibility(),
      pinnedColumns: viewManager.getPinnedColumns(),
    };

    // Update directly using the ID
    viewState.updateViewById(currentView.id, updatedView);
    viewState.setActiveView(updatedView);
    viewState.setViewModifications([]);

    showSnackbar({
      title: 'Success',
      message: `View "${currentView.name}" updated successfully`,
      type: SnackbarType.success,
    });
  }, [
    viewManager,
    searchTerm,
    autosizeColumns,
    selectedRows,
    showOnlyIllioDifferences,
    hideEmptyDateColumns,
    isGroupingEnabled,
    groups,
    getCurrentDateColumns,
    viewState,
  ]);

  // Handle pinned columns change to ensure checkbox column is always first
  const handlePinnedColumnsChange = useCallback(
    (pinnedColumns: GridPinnedColumns) => {
      const checkboxField = GRID_CHECKBOX_SELECTION_COL_DEF.field;

      // Check if left pinned columns exist and if checkbox is NOT the first element
      if (pinnedColumns.left && pinnedColumns.left[0] !== checkboxField) {
        // Remove checkbox from anywhere in the array and add it as first element
        const otherColumns = pinnedColumns.left.filter(
          col => col !== checkboxField
        );
        const correctedPinnedColumns: GridPinnedColumns = {
          ...pinnedColumns,
          left: [checkboxField, ...otherColumns],
        };

        // Immediately correct the pinned columns order
        apiRef.current.setPinnedColumns(correctedPinnedColumns);
      }
    },
    [apiRef]
  );

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box>
        <PageHeader title="OTC" subtitle="Manage your OTC assets" />
      </Box>

      {/* Development Mode Warning */}
      <DevelopmentWarningBanner />

      <Paper
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          flex: 1,
          paddingTop: 2,
          minHeight: 0,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            paddingX: 2,
          }}
        >
          <Toolbar
            searchTerm={searchTerm}
            onSearch={handleSearch}
            disabled={loading}
            buttons={[
              {
                variant: 'contained',
                label: illioUploadLoading ? 'Uploading...' : 'Upload',
                onClick: handleIllioUpload,
                icon: illioUploadLoading ? (
                  <CircularProgress size={16} />
                ) : (
                  <CloudUploadIcon />
                ),
                disabled:
                  loading ||
                  illioUploadLoading ||
                  (selectedRows.length === 0 &&
                    Object.keys(updatedCells).length === 0),
                tooltip: 'Send changes to Illio',
              },
              {
                variant: 'text',
                label: illioCheckLoading
                  ? 'Checking...'
                  : 'Check Diffs',
                onClick: checkAllIllioDifferences,
                icon: illioCheckLoading ? (
                  <CircularProgress size={16} />
                ) : (
                  <CompareArrowsIcon />
                ),
                disabled: loading || illioCheckLoading || otcData.length === 0,
                color: 'secondary',
                tooltip: 'Compare with Illio database',
              },
              {
                variant: 'text',
                label: 'Views',
                onClick: viewManager.openSavedViewsPanel,
                icon: <ViewListIcon />,
                disabled: loading,
                color: 'secondary',
                tooltip: 'Load saved view configurations',
              },
              {
                variant: 'text',
                label: 'Column',
                onClick: () => setModalOpen(true),
                icon: <AddIcon />,
                disabled: loading,
                color: 'secondary',
                tooltip: 'Add custom columns to table',
                sx: { '& .MuiButton-startIcon': { marginRight: '2px' } },
              },
              {
                variant: 'text',
                label: 'Groups',
                onClick: handleOpenGroupsModal,
                icon: <LayersIcon />,
                disabled: loading,
                color: 'secondary',
                tooltip: 'Organize instruments into groups',
              },
            ]}
            menuItems={[
              {
                label: 'Save View',
                onClick: viewManager.openSaveDialog,
                icon: <SaveIcon />,
                disabled: loading,
                tooltip: 'Save current filters and settings',
              },
              {
                label: syncLoading ? 'Syncing...' : 'Sync All Instruments',
                onClick: handleSyncClick,
                icon: syncLoading ? (
                  <CircularProgress size={16} />
                ) : (
                  <SyncIcon />
                ),
                disabled: loading || syncLoading,
                badge: lastSyncDate
                  ? { content: formatLastSyncDate(lastSyncDate) }
                  : undefined,
                tooltip: 'Synchronize all instruments from Illio',
              },
              {
                label: 'Download Current View',
                onClick: handleExport,
                icon: <CloudDownload />,
                disabled: loading,
                tooltip: 'Download current view as Excel file',
              },
              {
                label: isDownloadingBackup
                  ? 'Downloading...'
                  : 'Download Backup',
                onClick: handleDownloadBackup,
                icon: isDownloadingBackup ? (
                  <CircularProgress size={16} />
                ) : (
                  <CloudDownload />
                ),
                disabled: isDownloadingBackup,
                tooltip: 'Download backup file of OTC data',
              },
              {
                label: isRestoringBackup ? 'Restoring...' : 'Restore Backup',
                onClick: handleRestoreBackup,
                icon: isRestoringBackup ? (
                  <CircularProgress size={16} />
                ) : (
                  <UploadFileIcon />
                ),
                disabled: isRestoringBackup,
                tooltip: 'Restore OTC data from backup file',
              },
            ]}
          />
        </Box>

        {/* Filter Controls */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            paddingX: 2,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              flexWrap: 'wrap',
            }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={showOnlyIllioDifferences}
                  onChange={() => toggleIllioDifferencesFilter()}
                  disabled={loading}
                />
              }
              label="Updates Only"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={hideEmptyDateColumns}
                  onChange={() => toggleHideEmptyDateColumns()}
                  disabled={loading}
                />
              }
              label="Hide Empties"
            />
            <AutosizeColumnsToggle
              apiRef={apiRef}
              checked={autosizeColumns}
              onChange={setAutosizeColumns}
              disabled={loading}
            />
            <GroupToggle
              checked={isGroupingEnabled}
              onChange={handleToggleGrouping}
              disabled={groups.length === 0}
            />
            <InactiveInstrumentsToggle
              checked={showInactiveInstruments}
              onChange={setShowInactiveInstruments}
              disabled={loading}
            />
            <UndoRedoButtons
              canUndo={canUndo}
              canRedo={canRedo}
              onUndo={undo}
              onRedo={redo}
              getUndoActionInfo={getUndoActionInfo}
              getRedoActionInfo={getRedoActionInfo}
              disabled={loading}
            />

            {/* Reset Table Button */}
            <CustomButton
              variant="text"
              startIcon={<RestartAltIcon />}
              onClick={handleClearActiveView}
              disabled={loading}
              color="secondary"
            >
              Reset
            </CustomButton>
          </Box>
          {viewManager.activeView && (
            <ActiveViewIndicator
              view={viewManager.activeView}
              modifications={viewManager.viewModifications}
              onClose={handleClearActiveView}
              onUpdateView={handleUpdateCurrentView}
            />
          )}
        </Box>

        {/* OTC Sync Progress Panel */}
        <Box sx={{ paddingX: 2 }}>
          <OTCSyncProgress />
        </Box>

        {/* OTC Check Differences Progress Panel */}
        <Box sx={{ paddingX: 2 }}>
          <OTCCheckProgress
            onRetryFailed={retryFailedOtcCheck}
            retryCount={failedOtcIds.length}
          />
        </Box>

        <Box sx={{ flex: 1, minHeight: 400, paddingX: 2, paddingBottom: 2 }}>
          {!loading &&
          !syncLoading &&
          !illioCheckLoading &&
          !isRestoringBackup &&
          otcData.length === 0 ? (
            <EmptyState
              icon={
                <AttachMoneyIcon
                  sx={{ fontSize: 80, color: 'text.secondary' }}
                />
              }
              title="No OTC Instruments"
              description="Upload your first data feed to begin managing OTC assets"
            />
          ) : (
            <StyledDataGrid
              key={isGroupingEnabled ? 'grouped-grid' : 'normal-grid'}
              apiRef={apiRef}
              rows={baseDisplayData}
              rowCount={actualRowCount}
              columns={columns}
              loading={
                loading || syncLoading || illioCheckLoading || isRestoringBackup
              }
              checkboxSelection
              isRowSelectable={params => !params.row.isGroup}
              disableRowSelectionOnClick
              processRowUpdate={processRowUpdate}
              getRowClassName={params =>
                params.row.isGroup ? 'group-row' : 'data-row'
              }
              onCellClick={handleCellClick}
              onCellDoubleClick={handleCellDoubleClick}
              onRowSelectionModelChange={handleRowSelectionChange}
              rowSelectionModel={gridSelectionModel}
              onColumnVisibilityModelChange={
                handleColumnVisibilityChangeWrapper
              }
              onPinnedColumnsChange={handlePinnedColumnsChange}
              onStateChange={createStateChangeHandler(
                apiRef,
                isGroupingEnabled
              )}
              onSortModelChange={(newSortModel, details) => {
                setSortModel(newSortModel);
                // Only update view modifications if we have an applied view and it's not a programmatic change
                if (
                  !illioCheckLoading &&
                  !isProgrammaticUpdate &&
                  viewManager.activeView &&
                  details.reason === 'uiRequestedSorting'
                ) {
                  // Update view modifications instead of clearing the view
                  requestAnimationFrame(() => {
                    updateViewModifications();
                  });
                }
              }}
              sortModel={sortModel}
              getCellClassName={getCellClassName}
              getRowId={getRowId}
              initialState={{
                pinnedColumns: {
                  left: [
                    GRID_CHECKBOX_SELECTION_COL_DEF.field,
                    ...(isGroupingEnabled
                      ? [GRID_TREE_DATA_GROUPING_FIELD, 'group_name']
                      : []),
                  ],
                },
              }}
              columnVisibilityModel={Object.fromEntries(
                Array.from(hiddenColumnFields).map(field => [field, false])
              )}
              slots={{
                columnMenu: CustomColumnMenu,
                footerRowCount: CustomGridFooter,
              }}
              slotProps={{
                columnMenu: {
                  onDeleteColumnClick: handleDeleteColumnClick,
                },
                loadingOverlay: {
                  variant: 'linear-progress',
                },
                footerRowCount: {
                  isGroupingEnabled,
                  groups,
                },
                cell: {
                  onMouseEnter: handleCellMouseEnter,
                  onMouseLeave: handleCellMouseLeave,
                },
              }}
              treeData={isGroupingEnabled}
              getTreeDataPath={isGroupingEnabled ? getTreeDataPath : undefined}
              groupingColDef={groupingColDef}
              defaultGroupingExpansionDepth={isGroupingEnabled ? 1 : undefined}
              sx={{
                ...DATAGRID_STYLES,
                [`& .${CSS_CLASSES.HIGHLIGHTED_CELL_ADDED}`]: {
                  position: 'relative',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    top: '2px',
                    right: '2px',
                    width: '12px',
                    height: '12px',
                    borderRadius: '50%',
                    background: '#4CAF50', // Solid bright green for added
                    border: '2px solid #ffffff',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
                    zIndex: 1,
                  },
                },
                [`& .${CSS_CLASSES.HIGHLIGHTED_CELL_REMOVED}`]: {
                  position: 'relative',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    top: '2px',
                    right: '2px',
                    width: '12px',
                    height: '12px',
                    borderRadius: '50%',
                    background: '#F44336', // Solid bright red for removed
                    border: '2px solid #ffffff',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
                    zIndex: 1,
                  },
                },
                [`& .${CSS_CLASSES.HIGHLIGHTED_CELL_CHANGED}`]: {
                  position: 'relative',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    top: '2px',
                    right: '2px',
                    width: '12px',
                    height: '12px',
                    borderRadius: '50%',
                    background: '#FF9800', // Solid bright orange for changed
                    border: '2px solid #ffffff',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
                    zIndex: 1,
                  },
                },
              }}
            />
          )}
        </Box>

        {/* Illio Value Tooltip Popover */}
        <Popover
          sx={{ pointerEvents: 'none' }}
          open={Boolean(tooltipAnchor)}
          anchorEl={tooltipAnchor}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          onClose={handleCellMouseLeave}
          disableRestoreFocus
        >
          <Typography sx={{ p: 1 }}>{tooltipValue}</Typography>
        </Popover>

        {/* Save View Dialog */}
        <SaveViewDialog
          open={viewManager.isSaveViewDialogOpen}
          editingView={viewManager.editingView}
          currentSearchTerm={searchTerm}
          selectedRows={selectedRows}
          showOnlyIllioDifferences={showOnlyIllioDifferences}
          hideEmptyDateColumns={hideEmptyDateColumns}
          autosizeColumns={autosizeColumns}
          isGroupingEnabled={isGroupingEnabled}
          autoShowNewDates={true}
          columnFilters={viewManager.getColumnFilters()}
          sortModel={viewManager.getSortModel()}
          columnVisibility={viewManager.getColumnVisibility()}
          pinnedColumns={viewManager.getPinnedColumns()}
          columns={columns}
          savedViews={viewManager.savedViews}
          editMode={viewManager.editMode}
          onSave={(viewName, autoShowNewDates) => {
            saveView(viewName, autoShowNewDates);
          }}
          onClose={() => {
            viewManager.closeSaveDialog();
          }}
        />

        {/* Saved Views Panel */}
        <SavedViewsPanel
          open={viewManager.isSavedViewsPanelOpen}
          onClose={viewManager.closeSavedViewsPanel}
          views={viewManager.savedViews}
          onViewClick={async view => {
            // Apply the new view with OTC-specific state
            await applyOTCView(view);
            viewManager.closeSavedViewsPanel();
          }}
          onSaveClick={() => {
            viewManager.openSaveDialog();
            viewManager.closeSavedViewsPanel();
          }}
          onMenuClick={viewManager.handleMenuClick}
          activeView={viewManager.activeView}
        />

        {/* View Context Menu */}
        <ViewContextMenu
          anchorEl={viewManager.contextMenuAnchor}
          open={Boolean(viewManager.contextMenuAnchor)}
          onClose={viewManager.handleCloseContextMenu}
          onApply={async () => {
            const view = viewManager.savedViews.find(
              v => v.id === viewManager.selectedViewId
            );
            if (view) {
              // Apply the new view with OTC-specific state
              await applyOTCView(view);
              viewManager.closeSavedViewsPanel(); // Close the modal after applying from menu
            }
          }}
          onRename={() => {
            const view = viewManager.savedViews.find(
              v => v.id === viewManager.selectedViewId
            );
            if (view) viewManager.handleRenameView();
          }}
          onUpdate={() => {
            const view = viewManager.savedViews.find(
              v => v.id === viewManager.selectedViewId
            );
            if (view) viewManager.handleUpdateView();
          }}
          onDelete={() => {
            if (viewManager.selectedViewId) viewManager.handleDeleteView();
          }}
        />

        {/* Confirmation Modal */}
        {deleteColumnConfirmation && (
          <ConfirmationModal
            open={deleteColumnConfirmation.open}
            title="Delete Column"
            message={`Are you sure you want to delete the column "${deleteColumnConfirmation.columnName}"?`}
            confirmText="Delete"
            onConfirm={handleConfirmDeleteColumn}
            onCancel={() => setDeleteColumnConfirmation(null)}
            severity="error"
          />
        )}

        {/* Sync Confirmation Modal */}
        <ConfirmationModal
          open={showSyncConfirmation}
          title="Sync Instruments"
          message="Local changes will be overwritten"
          confirmText="Sync"
          onConfirm={confirmSyncFromIllio}
          onCancel={() => setShowSyncConfirmation(false)}
          severity="warning"
        />
      </Paper>

      <AddColumnModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onAdd={handleAddColumn}
      />

      <ManageGroupsModal
        open={isGroupsModalOpen}
        onClose={handleCloseGroupsModal}
        onSave={handleSaveGroups}
        availableRows={availableRowsForGroups}
        existingGroups={groups}
        title="Manage Groups"
        columns={groupModalColumns}
        hiddenColumns={modalHiddenColumns}
      />
    </Box>
  );
};
